import { useState, useEffect, useRef } from 'react'
import { Play, Pause, Volume2, VolumeX, Maximize, AlertCircle, CheckCircle, Lock } from 'lucide-react'
import { getVideoInfo, getVideoPlatform } from '../../lib/videoUtils'
import { 
  initializeScreenRecordingPrevention, 
  setVideoAsProtected,
  getRecordingAttemptCount,
  hasExceededRecordingAttemptThreshold,
  pauseVideo,
  logSecurityEvent,
} from '../../lib/screenRecordingPrevention'

interface VideoPlayerProps {
  videoUrl: string
  title: string
  onProgress?: (progress: number) => void
  onTimeUpdate?: (currentTime: number) => void
  onComplete?: () => void
  autoplay?: boolean
  className?: string
  isCompleted?: boolean
  onManualComplete?: () => void
  courseId?: string
  lessonId?: string
}

export default function VideoPlayer({
  videoUrl,
  title,
  onProgress,
  onTimeUpdate,
  onComplete,
  autoplay = false,
  className = "",
  isCompleted = false,
  onManualComplete,
  courseId = "",
  lessonId = "",
}: VideoPlayerProps) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const [progress, setProgress] = useState(0)
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [recordingAttempts, setRecordingAttempts] = useState(0)
  const [isProtected, setIsProtected] = useState(true)
  
  const videoRef = useRef<HTMLVideoElement>(null)
  const playerRef = useRef<HTMLDivElement>(null)

  // Get video information using utilities
  const videoInfo = getVideoInfo(videoUrl)
  const platform = getVideoPlatform(videoUrl)

  const handleVideoProgress = () => {
    if (videoRef.current) {
      const video = videoRef.current
      const currentProgress = (video.currentTime / video.duration) * 100
      setProgress(currentProgress)
      setCurrentTime(video.currentTime)

      // Call time update callback
      onTimeUpdate?.(video.currentTime)

      // Only update progress if it's valid and video is being actively watched
      // More conservative approach - only track progress when user is actually watching
      if (currentProgress > 10 && currentProgress < 90 && !video.paused && !video.seeking && video.currentTime > 5) {
        onProgress?.(currentProgress)
      }

      // REMOVED auto-completion logic - only trigger on actual video end
      // This prevents premature completion
    }
  }

  const handleVideoEnd = () => {
    setIsPlaying(false)
    onComplete?.()
  }

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause()
      } else {
        videoRef.current.play()
      }
      setIsPlaying(!isPlaying)
    }
  }

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted
      setIsMuted(!isMuted)
    }
  }

  const toggleFullscreen = () => {
    if (playerRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen()
      } else {
        playerRef.current.requestFullscreen()
      }
    }
  }

  const formatTime = (time: number): string => {
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (videoRef.current) {
      const rect = e.currentTarget.getBoundingClientRect()
      const clickX = e.clientX - rect.left
      const newTime = (clickX / rect.width) * duration
      videoRef.current.currentTime = newTime
    }
  }

  useEffect(() => {
    if (videoRef.current) {
      const video = videoRef.current
      
      const handleLoadStart = () => setLoading(true)
      const handleCanPlay = () => setLoading(false)
      const handleError = () => {
        setError('Failed to load video')
        setLoading(false)
      }
      const handleLoadedMetadata = () => {
        setDuration(video.duration)
      }

      video.addEventListener('loadstart', handleLoadStart)
      video.addEventListener('canplay', handleCanPlay)
      video.addEventListener('error', handleError)
      video.addEventListener('loadedmetadata', handleLoadedMetadata)
      video.addEventListener('timeupdate', handleVideoProgress)
      video.addEventListener('ended', handleVideoEnd)
      video.addEventListener('play', () => setIsPlaying(true))
      video.addEventListener('pause', () => setIsPlaying(false))

      return () => {
        video.removeEventListener('loadstart', handleLoadStart)
        video.removeEventListener('canplay', handleCanPlay)
        video.removeEventListener('error', handleError)
        video.removeEventListener('loadedmetadata', handleLoadedMetadata)
        video.removeEventListener('timeupdate', handleVideoProgress)
        video.removeEventListener('ended', handleVideoEnd)
        video.removeEventListener('play', () => setIsPlaying(true))
        video.removeEventListener('pause', () => setIsPlaying(false))
      }
    }
  }, [])

  // Initialize screen recording prevention
  useEffect(() => {
    // Initialize protection for this video
    initializeScreenRecordingPrevention({
      enableDetection: true,
      enableWarnings: true,
      blockAPIs: true,
      blockKeyboardShortcuts: true,
      showWarningOverlay: true,
      logAttempts: true,
      pauseVideoOnThreshold: true,
      courseId,
      lessonId,
    })

    // Mark video as protected
    if (videoRef.current) {
      setVideoAsProtected(videoRef.current)
      setIsProtected(true)
    }

    // Monitor recording attempts - more aggressive
    const attemptMonitor = setInterval(() => {
      const attempts = getRecordingAttemptCount()
      setRecordingAttempts(attempts)

      // Log to backend if attempts are detected
      if (attempts > 0 && attempts % 2 === 0 && courseId && lessonId) {
        logSecurityEvent('screen_recording_attempt', courseId, lessonId)
      }

      // Threshold exceeded - pause video
      if (hasExceededRecordingAttemptThreshold()) {
        console.warn('🚫 Recording attempt threshold exceeded - pausing video')
        if (videoRef.current && !videoRef.current.paused) {
          videoRef.current.pause()
        }
      }
    }, 2000) // Check every 2 seconds for faster response

    return () => clearInterval(attemptMonitor)
  }, [courseId, lessonId])

  // YouTube Player Component
  if (platform === 'youtube') {
    if (!videoInfo) {
      return (
        <div className={`bg-black flex items-center justify-center ${className}`}>
          <div className="text-center text-white">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg">Invalid YouTube URL</p>
          </div>
        </div>
      )
    }

    return (
      <div ref={playerRef} className={`relative ${className}`} style={{ aspectRatio: '16/9' }}>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black">
            <div className="w-8 h-8 border-4 border-white/20 rounded-full animate-spin border-t-white"></div>
          </div>
        )}
        <iframe
          src={videoInfo.embedUrl}
          className="absolute top-0 left-0 w-full h-full"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; web-share"
          allowFullScreen
          title={title}
          onLoad={() => setLoading(false)}
          onError={() => setError('Failed to load YouTube video')}
        />
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-black">
            <div className="text-center text-white">
              <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg">{error}</p>
            </div>
          </div>
        )}
      </div>
    )
  }

  // Vimeo Player Component
  if (platform === 'vimeo') {
    if (!videoInfo) {
      return (
        <div className={`bg-black flex items-center justify-center ${className}`}>
          <div className="text-center text-white">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg">Invalid Vimeo URL</p>
          </div>
        </div>
      )
    }

    return (
      <div ref={playerRef} className={`relative ${className}`} style={{ aspectRatio: '16/9' }}>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black">
            <div className="w-8 h-8 border-4 border-white/20 rounded-full animate-spin border-t-white"></div>
          </div>
        )}
        <iframe
          src={videoInfo.embedUrl}
          className="absolute top-0 left-0 w-full h-full"
          frameBorder="0"
          allow="autoplay; fullscreen"
          allowFullScreen
          title={title}
          onLoad={() => setLoading(false)}
          onError={() => setError('Failed to load Vimeo video')}
        />
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-black">
            <div className="text-center text-white">
              <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg">{error}</p>
            </div>
          </div>
        )}
      </div>
    )
  }

  // Google Drive Player Component
  if (platform === 'googledrive') {
    if (!videoInfo) {
      return (
        <div className={`bg-black flex items-center justify-center ${className}`}>
          <div className="text-center text-white">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg">Invalid Google Drive URL</p>
          </div>
        </div>
      )
    }

    return (
      <div ref={playerRef} className={`relative ${className}`} style={{ aspectRatio: '16/9' }}>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black">
            <div className="w-8 h-8 border-4 border-white/20 rounded-full animate-spin border-t-white"></div>
          </div>
        )}
        
        <iframe
          src={videoInfo.embedUrl}
          className="absolute top-0 left-0 w-full h-full"
          frameBorder="0"
          allow="autoplay; encrypted-media"
          allowFullScreen
          title={title}
          sandbox="allow-same-origin allow-scripts allow-presentation"
          referrerPolicy="no-referrer-when-downgrade"
          onLoad={() => setLoading(false)}
          onError={() => setError('Failed to load Google Drive video')}
        />
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-black">
            <div className="text-center text-white">
              <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg">{error}</p>
              <p className="text-sm mt-2 opacity-75">
                Make sure the Google Drive file is shared publicly
              </p>
            </div>
          </div>
        )}
      </div>
    )
  }

  // HTML5 Video Player Component (for direct video files)
  return (
    <div ref={playerRef} className={`relative bg-black group ${className}`} style={{ aspectRatio: '16/9' }}>
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black">
          <div className="w-8 h-8 border-4 border-white/20 rounded-full animate-spin border-t-white"></div>
        </div>
      )}
      
      {error ? (
        <div className="absolute inset-0 flex items-center justify-center bg-black">
          <div className="text-center text-white">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg">{error}</p>
          </div>
        </div>
      ) : (
        <>
          <video
            ref={videoRef}
            className="absolute top-0 left-0 w-full h-full object-contain"
            src={videoUrl}
            autoPlay={autoplay}
            controlsList="nodownload"
            data-protected-video="true"
            onContextMenu={(e) => e.preventDefault()}
            playsInline
          />
          
          {/* Protection Indicator */}
          <div className="absolute top-4 right-4 flex items-center gap-2 bg-black/60 backdrop-blur-sm px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity z-10">
            <Lock className="w-4 h-4 text-green-400" />
            <span className="text-xs text-green-400 font-medium">Protected</span>
            {recordingAttempts > 0 && (
              <div className="ml-2 pl-2 border-l border-green-400/50">
                <span className="text-xs text-yellow-400">⚠️ {recordingAttempts} attempt{recordingAttempts !== 1 ? 's' : ''}</span>
              </div>
            )}
          </div>
          
          {/* Custom Controls */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 opacity-0 group-hover:opacity-100 transition-opacity">
            {/* Progress Bar */}
            <div 
              className="w-full h-1 bg-white/30 rounded-full mb-4 cursor-pointer"
              onClick={handleProgressClick}
            >
              <div 
                className="h-full bg-rose-500 rounded-full transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
            
            {/* Controls */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={togglePlay}
                  className="text-white hover:text-rose-400 transition-colors"
                >
                  {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
                </button>
                
                <button
                  onClick={toggleMute}
                  className="text-white hover:text-rose-400 transition-colors"
                >
                  {isMuted ? <VolumeX className="w-6 h-6" /> : <Volume2 className="w-6 h-6" />}
                </button>
                
                <div className="text-white text-sm">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </div>
              </div>
              
              <button
                onClick={toggleFullscreen}
                className="text-white hover:text-rose-400 transition-colors"
              >
                <Maximize className="w-6 h-6" />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}